import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Send, 
  Pin, 
  AlertTriangle, 
  ClipboardList, 
  Users, 
  Sparkles, 
  Trash2, 
  Maximize2,
  CheckCircle2,
  Clock,
  Shield,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { 
  AdminChatMessage, 
  ChatChannel, 
  MessagePriority, 
  sendAdminMessage, 
  subscribeToAdminMessages, 
  togglePinMessage, 
  deleteAdminMessage 
} from '../../services/adminChatService';
import { 
  AdminPresenceRecord, 
  subscribeToAdminPresence 
} from '../../services/adminPresenceService';

interface AdminChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenFullView?: () => void;
}

export const AdminChatDrawer: React.FC<AdminChatDrawerProps> = ({
  isOpen,
  onClose,
  onOpenFullView,
}) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<AdminChatMessage[]>([]);
  const [onlineAdmins, setOnlineAdmins] = useState<AdminPresenceRecord[]>([]);
  const [activeChannel, setActiveChannel] = useState<ChatChannel | 'all'>('all');
  const [inputText, setInputText] = useState('');
  const [priority, setPriority] = useState<MessagePriority>('normal');
  const [channelSelect, setChannelSelect] = useState<ChatChannel>('broadcast');
  const [sending, setSending] = useState(false);
  const [showRoster, setShowRoster] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Subscribe to messages
  useEffect(() => {
    if (!isOpen) return;
    const unsub = subscribeToAdminMessages(activeChannel, (newMsgs) => {
      setMessages(newMsgs);
    });
    return () => unsub();
  }, [isOpen, activeChannel]);

  // Subscribe to online team presence
  useEffect(() => {
    if (!isOpen) return;
    const unsub = subscribeToAdminPresence((presences) => {
      setOnlineAdmins(presences.filter((p) => p.status === 'online' || p.status === 'away'));
    });
    return () => unsub();
  }, [isOpen]);

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || !currentUser || sending) return;

    setSending(true);
    try {
      await sendAdminMessage({
        senderUid: currentUser.uid,
        senderEmail: currentUser.email,
        senderName: currentUser.displayName || currentUser.email.split('@')[0],
        senderRole: currentUser.role || 'super_admin',
        senderAvatar: currentUser.avatarUrl,
        text: inputText.trim(),
        channel: channelSelect,
        priority,
      });

      setInputText('');
      setPriority('normal');
    } catch (err) {
      console.error('Failed to send admin message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleTogglePin = async (msgId: string, currentPin: boolean) => {
    try {
      await togglePinMessage(msgId, !currentPin);
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
  };

  const handleDelete = async (msgId: string) => {
    if (window.confirm('Delete this message for all admins?')) {
      try {
        await deleteAdminMessage(msgId);
      } catch (err) {
        console.error('Failed to delete message:', err);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
      />

      {/* Slide-over panel */}
      <div className="absolute inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-md sm:max-w-lg bg-[#10231d] border-l border-[#22493c] text-white flex flex-col shadow-2xl">
          {/* Header */}
          <div className="p-4 sm:p-5 border-b border-[#22493c] flex items-center justify-between bg-[#152a23]/60">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-brand-500/20 text-brand-400 border border-brand-500/30 flex items-center justify-center shadow-glow-sm">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm sm:text-base flex items-center gap-2">
                  <span>Admin Team Chat</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    {onlineAdmins.length} Online
                  </span>
                </h3>
                <p className="text-[11px] text-[#90cbb7]">
                  Shift handovers, escrow alerts & staff messages
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              {onOpenFullView && (
                <button
                  onClick={() => {
                    onClose();
                    onOpenFullView();
                  }}
                  title="Expand to Full Page View"
                  className="p-2 rounded-xl text-[#90cbb7] hover:text-white hover:bg-[#22493c] transition-colors"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 rounded-xl text-[#90cbb7] hover:text-white hover:bg-[#22493c] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Channels & Online Roster Bar */}
          <div className="px-4 py-2.5 bg-[#152a23]/30 border-b border-[#22493c] flex items-center justify-between gap-2 overflow-x-auto">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold">
              <button
                onClick={() => setActiveChannel('all')}
                className={`px-2.5 py-1 rounded-lg transition-colors ${
                  activeChannel === 'all' 
                    ? 'bg-brand-500 text-forest-void font-bold shadow-sm' 
                    : 'text-[#90cbb7] hover:text-white hover:bg-[#152a23]'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveChannel('handover')}
                className={`px-2.5 py-1 rounded-lg transition-colors flex items-center gap-1 ${
                  activeChannel === 'handover' 
                    ? 'bg-amber-500 text-forest-void font-bold shadow-sm' 
                    : 'text-amber-300/80 hover:text-amber-300 hover:bg-[#152a23]'
                }`}
              >
                <ClipboardList className="w-3 h-3" />
                Handover
              </button>
              <button
                onClick={() => setActiveChannel('escrow_alert')}
                className={`px-2.5 py-1 rounded-lg transition-colors flex items-center gap-1 ${
                  activeChannel === 'escrow_alert' 
                    ? 'bg-rose-500 text-white font-bold shadow-sm' 
                    : 'text-rose-300/80 hover:text-rose-300 hover:bg-[#152a23]'
                }`}
              >
                <AlertTriangle className="w-3 h-3" />
                Escrow Alerts
              </button>
            </div>

            <button
              onClick={() => setShowRoster(!showRoster)}
              className="text-[11px] text-[#90cbb7] hover:text-brand-400 font-bold flex items-center gap-1 shrink-0 px-2 py-1 rounded-lg hover:bg-[#152a23]"
            >
              <Users className="w-3.5 h-3.5" />
              <span>{onlineAdmins.length} staff</span>
            </button>
          </div>

          {/* Collapsible Active Staff List */}
          {showRoster && (
            <div className="p-3 bg-[#152a23] border-b border-[#22493c] text-xs">
              <div className="font-bold text-[#90cbb7] uppercase tracking-wider text-[10px] mb-2">
                Active Staff on Shift
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                {onlineAdmins.map((admin) => (
                  <div 
                    key={admin.uid}
                    className="p-2 rounded-xl bg-[#10231d] border border-[#22493c] flex items-center gap-2"
                  >
                    <div className="relative">
                      {admin.avatarUrl ? (
                        <img src={admin.avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-brand-500/20 text-brand-400 font-bold flex items-center justify-center text-xs">
                          {admin.displayName?.charAt(0) || 'A'}
                        </div>
                      )}
                      <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#10231d] ${
                        admin.status === 'online' ? 'bg-emerald-400' : 'bg-amber-400'
                      }`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold truncate text-[11px] text-white">{admin.displayName}</p>
                      <p className="text-[9px] text-[#90cbb7] truncate capitalize">{admin.role.replace('_', ' ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Messages Feed */}
          <div className="flex-1 p-4 space-y-3 overflow-y-auto">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 text-[#90cbb7]">
                <MessageSquare className="w-10 h-10 stroke-1 mb-2 text-[#90cbb7]/40" />
                <p className="text-sm font-semibold text-white">No messages in this channel yet</p>
                <p className="text-xs text-[#90cbb7]/70 mt-1 max-w-xs">
                  Leave a handover note, discuss a dispute, or alert your team about active escrows.
                </p>
              </div>
            ) : (
              messages.map((msg) => {
                const isMe = msg.senderUid === currentUser?.uid;
                const isHandover = msg.priority === 'handover';
                const isUrgent = msg.priority === 'urgent';

                return (
                  <div 
                    key={msg.id} 
                    className={`rounded-2xl p-3 text-xs transition-all relative group ${
                      msg.pinned 
                        ? 'bg-amber-500/10 border border-amber-500/30' 
                        : isUrgent
                          ? 'bg-rose-500/10 border border-rose-500/30'
                          : isHandover
                            ? 'bg-emerald-500/10 border border-emerald-500/25'
                            : isMe
                              ? 'bg-brand-500/10 border border-brand-500/20 ml-6'
                              : 'bg-[#152a23] border border-[#22493c] mr-6'
                    }`}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-white text-[11px]">
                          {isMe ? 'You' : msg.senderName}
                        </span>
                        <span className="text-[9px] px-1.5 py-0.2 rounded font-semibold bg-[#22493c] text-[#90cbb7] capitalize">
                          {msg.senderRole.replace('_', ' ')}
                        </span>
                        {isHandover && (
                          <span className="text-[9px] px-1.5 py-0.2 rounded font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            📋 Handover
                          </span>
                        )}
                        {isUrgent && (
                          <span className="text-[9px] px-1.5 py-0.2 rounded font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30 animate-pulse">
                            🚨 Urgent
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        {msg.pinned && (
                          <Pin className="w-3 h-3 text-amber-400 fill-amber-400" />
                        )}
                        <span className="text-[10px] text-[#90cbb7]/60">
                          {msg.createdAt?.toDate 
                            ? new Date(msg.createdAt.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : 'Just now'
                          }
                        </span>

                        {/* Actions */}
                        <div className="hidden group-hover:flex items-center gap-1 ml-1.5">
                          <button
                            onClick={() => handleTogglePin(msg.id, !!msg.pinned)}
                            title={msg.pinned ? 'Unpin' : 'Pin to top'}
                            className="p-1 rounded text-[#90cbb7] hover:text-white hover:bg-[#10231d]"
                          >
                            <Pin className="w-3 h-3" />
                          </button>
                          {(isMe || currentUser?.role === 'super_admin') && (
                            <button
                              onClick={() => handleDelete(msg.id)}
                              title="Delete message"
                              className="p-1 rounded text-rose-400 hover:text-rose-300 hover:bg-[#10231d]"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <p className="text-white/95 leading-relaxed text-[12px] whitespace-pre-wrap">
                      {msg.text}
                    </p>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Compose Form */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-[#22493c] bg-[#152a23]/60 space-y-3">
            {/* Tag Selection Row */}
            <div className="flex items-center justify-between text-[11px] gap-2">
              <div className="flex items-center gap-1.5">
                <span className="text-[#90cbb7]">Tag:</span>
                <button
                  type="button"
                  onClick={() => setPriority('normal')}
                  className={`px-2 py-0.5 rounded-md text-[10px] font-bold transition-colors ${
                    priority === 'normal' 
                      ? 'bg-brand-500 text-forest-void' 
                      : 'bg-[#10231d] text-[#90cbb7] border border-[#22493c]'
                  }`}
                >
                  Normal
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPriority('handover');
                    setChannelSelect('handover');
                  }}
                  className={`px-2 py-0.5 rounded-md text-[10px] font-bold transition-colors flex items-center gap-1 ${
                    priority === 'handover' 
                      ? 'bg-amber-500 text-forest-void' 
                      : 'bg-[#10231d] text-amber-300 border border-amber-500/30'
                  }`}
                >
                  <ClipboardList className="w-2.5 h-2.5" />
                  Handover
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPriority('urgent');
                    setChannelSelect('escrow_alert');
                  }}
                  className={`px-2 py-0.5 rounded-md text-[10px] font-bold transition-colors flex items-center gap-1 ${
                    priority === 'urgent' 
                      ? 'bg-rose-500 text-white' 
                      : 'bg-[#10231d] text-rose-300 border border-rose-500/30'
                  }`}
                >
                  <AlertTriangle className="w-2.5 h-2.5" />
                  Urgent
                </button>
              </div>

              <select
                value={channelSelect}
                onChange={(e) => setChannelSelect(e.target.value as ChatChannel)}
                className="bg-[#10231d] text-xs text-[#90cbb7] border border-[#22493c] rounded-lg px-2 py-0.5 outline-none"
              >
                <option value="broadcast">📢 All Staff</option>
                <option value="handover">📋 Shift Handover</option>
                <option value="escrow_alert">🚨 Escrow Alerts</option>
                <option value="disputes">⚖️ Dispute Desk</option>
              </select>
            </div>

            {/* Input & Send Button */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type a message or shift handover note..."
                className="flex-1 px-4 py-2.5 rounded-xl text-xs bg-[#10231d] border border-[#22493c] text-white placeholder-[#90cbb7]/60 outline-none focus:border-brand-500"
              />
              <button
                type="submit"
                disabled={!inputText.trim() || sending}
                className="p-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 disabled:opacity-40 text-forest-void font-bold transition-all shadow-glow-sm flex items-center justify-center shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
